import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  LockClosedIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CogIcon,
  BuildingOfficeIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';

const ClientAccessPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const features = [
    {
      icon: ChartBarIcon,
      title: "Real-Time Dashboards",
      description: "Live monitoring of your token's liquidity metrics, market performance, and exchange coverage."
    },
    {
      icon: CogIcon,
      title: "Market Maker Analytics",
      description: "Detailed performance tracking and benchmarking of your market making arrangements."
    },
    {
      icon: BuildingOfficeIcon,
      title: "Exchange Metrics",
      description: "Comprehensive analysis of venue performance, trading volumes, and optimization opportunities."
    },
    {
      icon: DocumentChartBarIcon,
      title: "Strategic Reports",
      description: "Monthly and quarterly strategic insights with actionable recommendations for liquidity optimization."
    }
  ];

  return (
    <div className="min-h-screen bg-primary-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                Client Portal
              </div>
              <h1 className="text-display font-serif font-bold text-cream-100 mb-6">
                Client Analytics Access –{' '}
                <span className="text-gradient">Coming Soon</span>
              </h1>
              <p className="text-body-lg text-cream-200 max-w-4xl mx-auto">
                This area will soon allow Valeon clients to securely access personalized dashboards 
                tracking their token performance, market maker coverage, and exchange metrics.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-16 bg-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                What to Expect
              </h2>
              <p className="text-cream-200 max-w-3xl mx-auto">
                Our client portal will provide secure, real-time access to comprehensive 
                analytics and strategic insights tailored to your token's liquidity performance.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="glass-card p-8 group hover:scale-105 transition-transform duration-300"
                >
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-cream-200 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security & Access */}
      <section className="py-16 bg-gradient-to-b from-primary-900 to-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-accent-blue to-accent-gold rounded-3xl flex items-center justify-center mb-6">
                    <ShieldCheckIcon className="h-10 w-10 text-white" />
                  </div>
                </div>
                
                <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                  Enterprise-Grade Security
                </h2>
                
                <div className="space-y-4 text-cream-200 leading-relaxed mb-8">
                  <p>
                    Our client portal will feature bank-level security protocols, multi-factor 
                    authentication, and encrypted data transmission to ensure your sensitive 
                    market data remains completely secure.
                  </p>
                  
                  <p>
                    Each client will receive personalized access credentials and role-based 
                    permissions, ensuring you only see data relevant to your specific engagements.
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <LockClosedIcon className="h-6 w-6 text-accent-blue" />
                  <span className="text-cream-300 text-sm">256-bit SSL encryption</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="glass-card p-12 text-center"
              >
                <div className="mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-accent-blue to-accent-gold rounded-full flex items-center justify-center mx-auto mb-6">
                    <LockClosedIcon className="h-12 w-12 text-white" />
                  </div>
                </div>
                
                <h3 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                  Ready to Get Started?
                </h3>
                
                <p className="text-cream-200 mb-8 leading-relaxed">
                  Interested in accessing our client portal? Request early access and we'll 
                  notify you as soon as your personalized dashboard is ready.
                </p>
                
                <button className="btn-primary mb-6">
                  Request Access
                </button>
                
                <div className="text-center">
                  <p className="text-cream-400 text-sm mb-2">
                    Current clients can contact their account manager
                  </p>
                  <a 
                    href="mailto:contact@valeon.io" 
                    className="text-accent-blue hover:text-accent-gold transition-colors duration-300 text-sm"
                  >
                    contact@valeon.io
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                Development Timeline
              </h2>
              <p className="text-cream-200 max-w-2xl mx-auto">
                We're working hard to bring you the most comprehensive client portal 
                in the token liquidity advisory space.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              <div className="space-y-8">
                {[
                  {
                    phase: "Phase 1",
                    title: "Foundation & Security",
                    status: "In Progress",
                    description: "Building secure authentication and core infrastructure"
                  },
                  {
                    phase: "Phase 2", 
                    title: "Real-Time Dashboards",
                    status: "Planning",
                    description: "Developing personalized analytics and performance tracking"
                  },
                  {
                    phase: "Phase 3",
                    title: "Advanced Analytics",
                    status: "Upcoming",
                    description: "Adding predictive insights and optimization recommendations"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.phase}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="glass-card p-6 flex items-center space-x-6"
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        item.status === 'In Progress' ? 'bg-accent-blue' :
                        item.status === 'Planning' ? 'bg-accent-warning' :
                        'bg-cream-400'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-heading-sm font-semibold text-cream-100">
                          {item.phase}: {item.title}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'In Progress' ? 'bg-accent-blue/20 text-accent-blue' :
                          item.status === 'Planning' ? 'bg-accent-warning/20 text-accent-warning' :
                          'bg-cream-400/20 text-cream-400'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-cream-300 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientAccessPage;